import React from "react";
import Card from "../../../components/common/card/card";
import Dropdown from "../../../components/common/dropdown/dropdown";
import Button from "../../../components/common/button/button";
import { InfoIcon } from "../../../assets/Icons";
import { IIconBasicProps } from "../../../assets/Icons/IIconBasicProps";
import { Category, OptionType } from "../ez-quote.types";
import { useTranslation } from "../../../contexts/TranslationContext";

interface CategoryStepProps {
  categories: Category[];
  selectedCategoryParent: Category | null;
  goldParentCategory: Category | null;
  selectedGoldItemType: OptionType | null;
  openDropdownId: string | null;
  setOpenDropdownId: (id: string | null) => void;
  handleCategoryPress: (cat: Category) => void;
  getIconComponent: (name: string | null) => React.FC<IIconBasicProps>;
  // Gold narrow step props:
  fetchAttributes: (id: number) => void;
  setCurrentStep: (step: number) => void;
  setIsEditing: (v: boolean) => void;
  restoringCategoryRef: React.MutableRefObject<Category | null>;
  itemTypeLabelRef: React.MutableRefObject<string | undefined>;
  setSelectedGoldItemType: (v: OptionType | null) => void;
  setGoldParentCategory: (v: Category | null) => void;
}

const CategoryStep: React.FC<CategoryStepProps> = ({
  categories,
  selectedCategoryParent,
  goldParentCategory,
  selectedGoldItemType,
  openDropdownId,
  setOpenDropdownId,
  handleCategoryPress,
  getIconComponent,
  fetchAttributes,
  setCurrentStep,
  setIsEditing,
  restoringCategoryRef,
  itemTypeLabelRef,
  setSelectedGoldItemType,
  setGoldParentCategory: _setGoldParentCategory,
}) => {
  const t = useTranslation();

  const renderGoldNarrowStep = () => {
    if (!goldParentCategory) return null;

    const goldCategory = selectedCategoryParent?.childCategories?.find((c) => c.category_name === "Gold");
    const sourceChildren = goldParentCategory.childCategories.length > 0
      ? goldParentCategory.childCategories
      : (goldCategory?.childCategories ?? []);
    const itemTypeOptions: OptionType[] = sourceChildren.map(
      (child) => ({ key: String(child.category_id), value: child.category_name })
    );

    const proceedAsOther = () => {
      let apiCategory;
      if (goldParentCategory.childCategories?.length) {
        const leafChild = goldParentCategory.childCategories.find((c) => c.is_leaf);
        apiCategory = leafChild ?? goldParentCategory;
      } else {
        const goldCat = selectedCategoryParent?.childCategories?.find((c) => c.category_name === "Gold");
        const leafChild = goldCat?.childCategories?.find((c) => c.is_leaf);
        apiCategory = leafChild ?? goldParentCategory;
      }
      restoringCategoryRef.current = apiCategory;
      // Display-only label carried via ref through async fetchAttributes callback
      itemTypeLabelRef.current = t.shared.otherNotSure;
      fetchAttributes(apiCategory.category_id);
      setCurrentStep(3);
      setIsEditing(false);
    };

    const handleGoldContinue = () => {
      if (!selectedGoldItemType) {
        setSelectedGoldItemType({ key: "other", value: t.shared.otherNotSure });
        proceedAsOther();
        return;
      }
      if (selectedGoldItemType.key === "other") {
        proceedAsOther();
        return;
      }
      // Silver/Platinum: no own children — resolve item type from Gold's children
      if (!goldParentCategory.childCategories?.length) {
        const goldCat = selectedCategoryParent?.childCategories?.find((c) => c.category_name === "Gold");
        const selectedChild = goldCat?.childCategories?.find(
          (child) => String(child.category_id) === selectedGoldItemType.key
        );
        if (selectedChild) {
          handleCategoryPress(selectedChild);
        }
        return;
      }
      const selectedChild = goldParentCategory.childCategories.find(
        (child) => String(child.category_id) === selectedGoldItemType.key
      );
      if (selectedChild) {
        handleCategoryPress(selectedChild);
      }
    };

    return (
      <div className="attributes-container">
        <div className="selected-category-card">
          <Card
            title={goldParentCategory.category_name}
            Icon={getIconComponent(goldParentCategory.icon_name)}
            className="category-card"
          />
        </div>
        <div className="attribute">
          <div className="attribute-name">
            {t.category.itemType}
          </div>
          <Dropdown
            id="gold-item-type"
            options={itemTypeOptions}
            placeholder={t.category.selectOption}
            value={selectedGoldItemType}
            onSelect={(option: OptionType) => setSelectedGoldItemType(option)}
            openDropdownId={openDropdownId}
            setOpenDropdownId={setOpenDropdownId}
            attributeName={t.category.itemType}
            showOthersOption={true}
            disableSearch={true}
          />
        </div>
        <div className="gold-instore-info">
          <InfoIcon />
          <div className="gold-instore-info-text">
            {t.category.goldInfoPre}{" "}
            <strong>{t.category.weight}</strong>
            {" "}{t.category.goldInfoAnd}{" "}
            <strong>{goldParentCategory.category_name === "Gold" ? t.category.karat : t.category.purity}</strong>
            {" "}{t.category.goldInfoPost}
          </div>
        </div>
        <div className="gold-instore-info-button">
          <Button
            label={t.shared.continue}
            onClick={handleGoldContinue}
            styleType="primary"
          />
        </div>
      </div>
    );
  };

  if (goldParentCategory) {
    return renderGoldNarrowStep();
  }

  return (
    <div>
      {selectedCategoryParent && (
        <div className="category-header">{selectedCategoryParent.category_name}</div>
      )}
      {categories.map((cat) => (
        <div className="category-list" key={cat.category_id}>
          <Card
            title={cat.category_name}
            Icon={getIconComponent(cat.icon_name)}
            onClick={() => handleCategoryPress(cat)}
            navigation={!cat.is_leaf}
          />
        </div>
      ))}
      <div className="categories-note">
        {t.category.cantFindNote}
      </div>
      <div className="store-visit-container">
        <a
          href="https://ezpawn.com/find-a-location/"
          target="_blank"
          rel="noreferrer"
          className="visit-store-link"
        >
          {t.category.visitStore}
        </a>
      </div>
    </div>
  );
};

export default CategoryStep;
